import React, {forwardRef, useRef} from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'

import {
  ToastProps as RadixToastProps,
  ToastViewportProps as RadixToastViewportProps,
  ToastProviderProps as RadixToastProviderProps,
  ToastTitleProps as RadixToastTitleProps,
  ToastDescriptionProps as RadixToastDescriptionProps,
  ToastActionProps as RadixToastActionProps,
  ToastCloseProps as RadixToastCloseProps
} from '@radix-ui/react-toast'

import {useRefObjectAsForwardedRef, sx, SxProp} from '../..'

// we take this step because in the future it's likely we will want to extend these
// as their own interfaces with our own additional props
export type ToastProps = RadixToastProps
/**
 * The toast that automatically closes. It should not be held open to acquire a user response.
 */
const Toast = forwardRef(({...props}: ToastProps, forwardedRef) => {
  const innerRef = useRef<HTMLLIElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return <ToastPrimitive.Root ref={innerRef} {...props} />
})

export interface ToastViewportProps extends RadixToastViewportProps {
  children?: React.ReactNode
}
/**
 * The toast that automatically closes. It should not be held open to acquire a user response.
 */
const Viewport = forwardRef(({children, ...props}: ToastViewportProps, forwardedRef) => {
  const innerRef = useRef<HTMLOListElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <ToastPrimitive.Viewport ref={innerRef} {...props}>
      {children}
    </ToastPrimitive.Viewport>
  )
})

export type ToastProviderProps = RadixToastProviderProps
/**
 * The provider that wraps your toasts and toast viewport. It usually wraps the application.
 */
const Provider = ({children, ...props}: ToastProviderProps) => {
  return <ToastPrimitive.Provider {...props}>{children}</ToastPrimitive.Provider>
}

export type ToastTitleProps = RadixToastTitleProps
/**
 * An optional title for the toast.
 */
const Title = forwardRef(({children, ...props}: ToastTitleProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <ToastPrimitive.Title ref={innerRef} {...props}>
      {children}
    </ToastPrimitive.Title>
  )
})

export type ToastDescriptionProps = RadixToastDescriptionProps
/**
 * The toast message.
 */
const Description = forwardRef(({children, ...props}: ToastDescriptionProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <ToastPrimitive.Description ref={innerRef} {...props}>
      {children}
    </ToastPrimitive.Description>
  )
})

export type ToastActionProps = RadixToastActionProps
/**
 * An action that is safe to ignore to ensure users are not expected to complete tasks with unexpected side effects as a result of a time limit.
 * When obtaining a user response is necessary, portal an AlertDialog styled as a toast into the viewport instead.
 */
const Action = forwardRef(({children, ...props}: ToastActionProps, forwardedRef) => {
  const innerRef = useRef<HTMLButtonElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <ToastPrimitive.Action {...props} ref={innerRef}>
      {children}
    </ToastPrimitive.Action>
  )
})

export type ToastCloseProps = RadixToastCloseProps
/**
 * A button that allows users to dismiss the toast before its duration has elapsed.
 */
const Close = forwardRef(({children, ...props}: ToastCloseProps, forwardedRef) => {
  const innerRef = useRef<HTMLButtonElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <ToastPrimitive.Close ref={innerRef} {...props}>
      {children}
    </ToastPrimitive.Close>
  )
})

// exports
Toast.displayName = 'Toast'
Viewport.displayName = 'Viewport'
Provider.displayName = 'Provider'
Title.displayName = 'Title'
Description.displayName = 'Description'
Action.displayName = 'Action'
Close.displayName = 'Close'

/** Toasts display a succinct message that is displayed temporarily. */
export default Object.assign(Toast, {Provider, Viewport, Title, Description, Action, Close})
